import React from "react";

import { Calculator } from "lucide-react";
import CTALarge from "../CTA/CTALarge.jsx";
import Seo from "../Seo.jsx";

import Tooltip from "./Tooltip";
import "./guides.css";
import guidesConfig from "../../config/guidesConfig.js";
import arbom from './images/arbom.png'
const ArbingGuide = () => {
  const meta = guidesConfig.arbingGuide?.seo || {};

  return (
    <div className="guides-content">
      <Seo
        title={meta.title}
        description={meta.description}
        url={guidesConfig.arbingGuide?.route || "/guides"}
      />


      <p>
        Arbing (short for arbitrage betting) is a way to make guaranteed profits by
        betting on all outcomes of an event when odds differ enough between
        bookmakers and exchanges. In matched betting, it’s a tool we have available, but it comes
        with risks. This guide explains everything you need to know, with examples,
        tips, and a clear warning: arbing can get your accounts gubbed.
      </p>

      <h2>🚨 Quick Warning Before We Begin</h2>
<h6 className="warn">
            <strong>Arbing kills accounts.</strong> If you’re matched betting, only arb on
        accounts you don’t care about — gubbed accounts or those that no longer get
        promos, and has no value for things like extra places or best odds guaranteed (BOG). Bookies hate arbing
        and will shut you down fast if they suspect it.
      </h6>
      <h2>What Is Arbing?</h2>
      <p>
        Arbing is when you place bets on all possible outcomes of an event in a way
        that guarantees a profit. 
      </p>
      <h6 className="info">This happens when bookmakers offer odds that are
      higher than the true market price — usually because of errors or slow updates.</h6>
      <p>It’s sometimes called:</p>
      <ul>
        <li>
          <Tooltip content="Also known as arbitrage betting">arbitrage betting</Tooltip>
        </li>
        <li>
          <Tooltip content="Also known as surebet">surebet</Tooltip>
        </li>
        <li>
          <Tooltip content="Also known as miraclebet">miraclebet</Tooltip>
        </li>
        <li>
          <Tooltip content="Also known as sports arbitrage">sports arbitrage</Tooltip>
        </li>
      </ul>
      <div className="example-container">
    <h4>example</h4>
    <p>Let’s say:</p>
      <ul>
        <li>William Hill are offering odds of <strong>3.8</strong> for Manchester United to win.</li>
        <li>Betfair exchange lets you lay Manchester United at <strong>2.88</strong>.</li>
      </ul>
      <p>
        That’s an arb. By backing at 3.8 and laying at 2.88, you can lock in a profit
        regardless of the outcome (see the formula below).
      </p>
    </div>  
      <p>If you use <a href="https://www.oddsmonkey.com/affiliates/affiliate.php?id=66317" target="_blank" rel="noreferrer">OddsMonkey</a> you can spot an arb because the bet rating will be in <span className="lay">red</span>.</p>
      <img src={arbom} alt="Arbing oddsmonkey" />
      

      <h2>Back/Lay Arbing vs Bookie-to-Bookie Arbing</h2>
      <p>There are two main types of arbing:</p>
      <ul>
        <li>
          <strong>Back/lay arbing</strong> – Back with a bookie, lay on an exchange
          (e.g. Betfair or Smarkets).
        </li>
        <li>
          <strong>Bookie-to-bookie arbing</strong> – Dutch arbing - Back different outcomes with
          different bookmakers.
        </li>
      </ul>
      <p>
        Back/lay arbing is more common in matched betting because of the flexibility
        exchanges offer.
      </p>

      <h2>How to Calculate an Arb</h2>
      <p>To work out your <strong>lay stake</strong>, use:</p>
      <pre><code>Back Stake × Back Odds ÷ (Lay Odds – Commission) = Lay Stake</code></pre>

<div className="example-container">
    <h4>example</h4>
     <p>
        You back Man United for <strong>£100 at 3.8</strong>. Betfair offers lay odds
        of <strong>2.88</strong>, with 5% commission.
      </p>
      <pre><code>100 × 3.8 ÷ (2.88 – 0.05) = £134.28 lay stake</code></pre>
      <p>
        No matter who wins, you’ll end up with a profit of around <strong>£27.50</strong>.
      </p>
    </div>     
    <CTALarge
        to="/calculators/arb-calculator"
        text="Try Our Arbing Calculator"
        icon={Calculator}
      />
      <h2>Why Do Arbs Exist?</h2>
      <p>Arbs appear when:</p>
      <ul>
        <li>Bookies disagree on odds.</li>
        <li>A big event (like an injury) happens and some bookies are slow to react.</li>
        <li>Odds move fast in volatile markets like horse racing.</li>
      </ul>
      <p>
        Bookmakers use software to track prices—but they’re not perfect. That’s where you
        come in.
      </p>

      <h2>Is Arbing Legal?</h2>
      <p>
        Yes. Arbing is completely legal. You’re just taking advantage of price
        differences, much like traders do in finance.
      </p>
      <h6 className="warn">
        But bookies don’t like it. If they catch you arbing, they’ll{" "}
        <Tooltip content="Restrict or close your account">gub</Tooltip> you—restrict
        stakes, remove offers, or close your account.
      </h6>

      <h2>When Should You Use Arbing?</h2>
      <p>Only arb when:</p>
      <ul>
        <li>You’ve been gubbed.</li>
        <li>The account has no long-term promo value.</li>
        <li>
          You’re doing shop arbing (
          <Tooltip content="Placing back bets in retail shops and laying online">sharbing</Tooltip>
          ) in retail stores.
        </li>
      </ul>
      <p>
        <strong>Never</strong> arb on a fresh or promo-active account. You’ll lose far
        more in lost promotions than you’ll gain in profit.
      </p>

      <h2>Best Events for Arbing</h2>
      <p>Arbs pop up in all sorts of markets, but some are better than others:</p>
      <ul>
        <li><strong>Horse racing</strong> – Lots of price movement, great for fast arbs.</li>
        <li><strong>Football</strong> – Huge volume, easier to stay under the radar.</li>
        <li><strong>Tennis</strong> – Simple two-way outcomes.</li>
      </ul>
      <h6 className="warn">
        Avoid niche or obscure markets – movement there
        screams arber activity. An arb on the Premier League might go un-noticed, but your bet on the Vietnamese Third Division Volleyball is going to raise eyebrows.
      </h6>

      <h2>Final Thoughts</h2>
      <p>
        Arbing is a fine strategy—<strong>for the right accounts</strong>. It’s perfect
        for squeezing value out of gubbed accounts or boosting returns once you’re
        promo-banned. But keep it separate from your main matched betting plan. Play it
        smart and think long term.
      </p>
    </div>
  );
};

export default ArbingGuide;
